// Drive three's splat order from a STABLE sort instead of its own.
//
// three's GaussianSplat sorts with a CountingSort whose scatter is an `atomicAdd`
// race, so the order within each of its depth bins is thread-arrival order and
// changes on every re-sort. With an environment in the same mesh that is visible
// flicker on geometry that never moved — the numbers are in the shader's header.
// This runs the repo's own stable LSD radix — the one `webgpu/wgsl/splat_sort.wgsl`
// already uses for exactly this reason — over the centres three is about to draw,
// and copies the resulting permutation into three's own order buffer. three's sort
// is then suppressed for that frame.
//
// It is NOT a second renderer: three still rasterises, still owns the mesh and the
// material. Only the permutation changes hands.
//
// Shape copied from the deleted renderer's serial sort, and both halves of it are
// load-bearing. The bind group layout is EXPLICIT rather than `layout: "auto"`,
// because auto mints a layout per pipeline with the bindings that pipeline does
// not reference PRUNED — rx_hist touches neither `P` nor `centre` — so one bind
// group could not serve all five kernels. And the four radix passes take four
// CONSTANT rx uniforms rather than one buffer rewritten per pass: `writeBuffer`
// is ordered against SUBMITS, so four writes before one submit would leave every
// pass reading the last one.

import shader from "./three_stable_sort.wgsl?raw";

const TILE = 256;
const U32 = 4;
/** Tiles per chunk in the two-level scan. 256 puts ~48 chunks on this scene:
 *  enough workgroups to fill the machine at level 1, few enough that level 2's
 *  single workgroup walks 48 entries rather than 12,104. */
const TILES_PER_CHUNK = 256;
/** Radix passes, and the first bit they start at. THREE passes from bit 8, not
 *  four from bit 0: the key is the bit pattern of a float radial distance, so the
 *  bottom byte is its last 8 mantissa bits — under 0.2 mm at this scene's far
 *  plane, against the 3.27 mm bins three's own sort uses. Splats that tie on the
 *  remaining 24 are ordered by INDEX, which is fixed for the session (the lift
 *  gives every texel a permanent slot, the environment never moves), so dropping
 *  the pass costs determinism nothing. Measured: 61 -> 46 ms, pixel-identical. */
const RADIX_PASSES = 3;
const FIRST_SHIFT = 8;

/** Bytes of the `Params` uniform. MUST match the WGSL struct, which is checked by
 *  tests/unit/threeStableSort.test.mjs — the first version wrote 48 bytes into a
 *  32-byte buffer and every sort failed validation with the page otherwise fine. */
export const PARAMS_BYTES = 48;

/** The sizing and routing a sort of `count` splats implies. Pure, so the two rules
 *  that are easy to get wrong can be tested without a GPU: how many chunks the
 *  two-level scan needs, and WHICH ping-pong buffer an odd pass count leaves the
 *  result in (copy the wrong one and the order is a half-sorted permutation —
 *  valid, drawable, and wrong). */
export function sortPlan(count) {
  const numTiles = Math.ceil(count / TILE);
  return {
    numTiles,
    slots: numTiles * TILE,
    numChunks: Math.ceil(numTiles / TILES_PER_CHUNK),
    passes: RADIX_PASSES,
    shifts: Array.from({ length: RADIX_PASSES }, (_, p) => FIRST_SHIFT + 8 * p),
    /** Which of the ping-pong pair holds the sorted refs after the last pass. */
    resultIsA: RADIX_PASSES % 2 === 0,
  };
}

export class ThreeStableSort {
  /** @param {GPUDevice} device */
  constructor(device) {
    this.device = device;
    this.slots = 0;
    this.numTiles = 0;
    this.buffers = null;
    this.centre = null;
  }

  /** Allocate for `count` splats. A no-op while the count is unchanged. */
  ensure(count) {
    const numTiles = Math.ceil(count / TILE);
    const slots = numTiles * TILE;
    if (this.buffers && this.slots === slots) return;
    this.dispose();

    const dev = this.device;
    const S = GPUBufferUsage.STORAGE;
    const U = GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST;
    const buf = (label, bytes, usage) => dev.createBuffer({ label, size: bytes, usage });

    const plan = sortPlan(count);
    this.slots = slots;
    this.numTiles = numTiles;
    this.numChunks = plan.numChunks;
    const b = {
      params: buf("tss_params", PARAMS_BYTES, U),
      centre: null,                         // three's, bound per sort
      keysA: buf("tss_keysA", slots * U32, S),
      refsA: buf("tss_refsA", slots * U32, S | GPUBufferUsage.COPY_SRC),
      keysB: buf("tss_keysB", slots * U32, S),
      refsB: buf("tss_refsB", slots * U32, S | GPUBufferUsage.COPY_SRC),
      tileHist: buf("tss_hist", slots * U32, S),
      tileOffset: buf("tss_offset", slots * U32, S),
      chunkBase: buf("tss_chunkbase", plan.numChunks * TILE * U32, S),
      // dir = pass&1 (0: A->B, 1: B->A). Constant for the session.
      rx: Array.from({ length: RADIX_PASSES }, (_, p) => {
        const r = buf(`tss_rx${p}`, 16, U);
        dev.queue.writeBuffer(r, 0, new Uint32Array([FIRST_SHIFT + 8 * p, p & 1, 0, 0]));
        return r;
      }),
    };
    this.buffers = b;

    const ro = "read-only-storage", rw = "storage", un = "uniform";
    const types = [un, ro, rw, rw, rw, rw, rw, rw, un, rw];
    this.layout = dev.createBindGroupLayout({
      label: "tss_bgl",
      entries: types.map((type, binding) => ({ binding, visibility: GPUShaderStage.COMPUTE, buffer: { type } })),
    });
    const module = dev.createShaderModule({ label: "three_stable_sort", code: shader });
    const pl = dev.createPipelineLayout({ bindGroupLayouts: [this.layout] });
    const mk = (entryPoint) => dev.createComputePipeline({
      label: `tss_${entryPoint}`, layout: pl, compute: { module, entryPoint },
    });
    this.pipelines = {
      clear: mk("clear_keys"), build: mk("build"), hist: mk("rx_hist"),
      scanChunks: mk("rx_scan_chunks"), scan: mk("rx_scan"), scatter: mk("rx_scatter"),
    };
    this.bindGroups = null;   // built once the centre buffer is known
  }

  /** One bind group per radix pass; they differ only in the rx uniform. Rebuilt
   *  when three rebuilds its mesh and hands us a different centre buffer. */
  _bind(centre) {
    if (this.bindGroups && this.centre === centre) return;
    this.centre = centre;
    const b = this.buffers;
    const shared = [b.params, centre, b.keysA, b.refsA, b.keysB, b.refsB, b.tileHist, b.tileOffset];
    this.bindGroups = b.rx.map((rx) => this.device.createBindGroup({
      layout: this.layout,
      entries: [...shared.map((buffer, binding) => ({ binding, resource: { buffer } })),
        { binding: 8, resource: { buffer: rx } },
        { binding: 9, resource: { buffer: b.chunkBase } }],
    }));
  }

  /**
   * Sort `count` splats by distance from `cameraPos` and copy the order into
   * `orderBuffer` (three's own `orderAttribute` buffer).
   * @returns {boolean} false when it could not run.
   */
  sort(centre, count, cameraPos, orderBuffer) {
    if (!centre || !orderBuffer || !(count > 0)) return false;
    this.ensure(count);
    this._bind(centre);
    const dev = this.device, b = this.buffers, p = this.pipelines, gs = this.bindGroups;

    const params = new ArrayBuffer(PARAMS_BYTES);
    new Uint32Array(params, 0, 4).set([count, this.slots, this.numTiles, TILES_PER_CHUNK]);
    new Float32Array(params, 16, 4).set([cameraPos.x, cameraPos.y, cameraPos.z, 0]);
    new Uint32Array(params, 32, 4).set([this.numChunks, 0, 0, 0]);
    dev.queue.writeBuffer(b.params, 0, params);

    const enc = dev.createCommandEncoder({ label: "three_stable_sort" });
    const pass = (label, pipe, group, groups) => {
      const cp = enc.beginComputePass({ label });
      cp.setPipeline(pipe); cp.setBindGroup(0, group); cp.dispatchWorkgroups(groups); cp.end();
    };
    // Pass 0's bind group carries rx.dir = 0, which is the direction clear/build
    // write in; neither kernel reads rx at all.
    // Only the padding past `count`; `build` writes the rest.
    const pad = this.slots - count;
    if (pad > 0) pass("tss_clear", p.clear, gs[0], Math.ceil(pad / 256));
    pass("tss_build", p.build, gs[0], Math.ceil(count / 256));
    // Ping-pong A<->B; an ODD number of passes leaves the result in B.
    for (let i = 0; i < RADIX_PASSES; i++) {
      pass(`tss_hist${i}`, p.hist, gs[i], this.numTiles);
      pass(`tss_scanc${i}`, p.scanChunks, gs[i], this.numChunks);
      pass(`tss_scan${i}`, p.scan, gs[i], 1);
      pass(`tss_scatter${i}`, p.scatter, gs[i], this.numTiles);
    }
    const sorted = sortPlan(count).resultIsA ? b.refsA : b.refsB;
    enc.copyBufferToBuffer(sorted, 0, orderBuffer, 0, Math.min(count * U32, orderBuffer.size));
    dev.queue.submit([enc.finish()]);
    return true;
  }

  dispose() {
    const b = this.buffers;
    if (!b) return;
    const all = [b.params, b.keysA, b.refsA, b.keysB, b.refsB, b.tileHist, b.tileOffset, b.chunkBase, ...(b.rx ?? [])];
    for (const buf of all) { try { buf?.destroy?.(); } catch { /* already gone */ } }
    this.buffers = null; this.pipelines = null; this.bindGroups = null;
    this.centre = null; this.slots = 0; this.numTiles = 0; this.numChunks = 0;
  }
}
